import { ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";

import { Button } from "@/common/components/ui/button";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
} from "@/common/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/common/components/ui/select";
import { cn } from "@/common/lib/utils";

export interface TablePaginationProps {
  /** Текущая страница (начиная с 1) */
  currentPage: number;
  /** Общее количество страниц */
  totalPages: number;
  /** Общее количество элементов */
  totalItems?: number;
  /** Количество элементов на странице */
  pageSize?: number;
  /** Варианты размера страницы */
  pageSizeOptions?: number[];
  /** Callback при изменении страницы */
  onPageChange: (page: number) => void;
  /** Callback при изменении размера страницы */
  onPageSizeChange?: (pageSize: number) => void;
  /** Дополнительные классы */
  className?: string;
  /** Показывать ли информацию о страницах */
  showInfo?: boolean;
  /** Показывать ли селектор размера страницы */
  showPageSizeSelector?: boolean;
}

export const TablePagination = React.forwardRef<HTMLDivElement, TablePaginationProps>(
  (
    {
      currentPage,
      totalPages,
      totalItems,
      pageSize,
      pageSizeOptions = [10, 20, 50, 100],
      onPageChange,
      onPageSizeChange,
      className,
      showInfo = true,
      showPageSizeSelector = false,
    },
    ref,
  ) => {
    const handlePrevious = () => {
      if (currentPage > 1) {
        onPageChange(currentPage - 1);
      }
    };

    const handleNext = () => {
      if (currentPage < totalPages) {
        onPageChange(currentPage + 1);
      }
    };

    const handlePageClick = (page: number) => {
      if (page >= 1 && page <= totalPages && page !== currentPage) {
        onPageChange(page);
      }
    };

    const getPageNumbers = () => {
      const pages: (number | "ellipsis")[] = [];
      const maxVisible = 7;

      if (totalPages <= maxVisible) {
        // Показываем все страницы
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Показываем первую страницу
        pages.push(1);

        if (currentPage <= 3) {
          // Если мы близко к началу
          for (let i = 2; i <= 4; i++) {
            pages.push(i);
          }
          pages.push("ellipsis");
          pages.push(totalPages);
        } else if (currentPage >= totalPages - 2) {
          // Если мы близко к концу
          pages.push("ellipsis");
          for (let i = totalPages - 3; i <= totalPages; i++) {
            pages.push(i);
          }
        } else {
          // Если мы в середине
          pages.push("ellipsis");
          for (let i = currentPage - 1; i <= currentPage + 1; i++) {
            pages.push(i);
          }
          pages.push("ellipsis");
          pages.push(totalPages);
        }
      }

      return pages;
    };

    const startItem = totalItems && pageSize ? (currentPage - 1) * pageSize + 1 : undefined;
    const endItem =
      totalItems && pageSize ? Math.min(currentPage * pageSize, totalItems) : undefined;

    // Show pagination if there are items or if page size selector is enabled
    // Hide only if there are no items and no page size selector
    if ((!totalItems || totalItems === 0) && !showPageSizeSelector) {
      return null;
    }

    const hasMultiplePages = totalPages > 1;

    return (
      <div
        ref={ref}
        className={cn(
          "flex flex-col gap-4 md:flex-row md:items-center md:justify-between",
          className,
        )}
      >
        {showInfo && (
          <div className="text-Text-secondary text-sm font-normal leading-5 hidden md:block">
            {totalItems && startItem && endItem ? (
              <>
                Показано {startItem}–{endItem} из {totalItems}
              </>
            ) : (
              <>
                Страница {currentPage} из {totalPages}
              </>
            )}
          </div>
        )}

        <div className="flex flex-col gap-4 md:flex-row md:items-center">
          {showPageSizeSelector && pageSize && onPageSizeChange && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Элементов на странице:</span>
              <Select
                value={pageSize.toString()}
                onValueChange={(value) => onPageSizeChange(Number(value))}
              >
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {pageSizeOptions.map((size) => (
                    <SelectItem key={size} value={size.toString()}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {hasMultiplePages && (
            <Pagination className="flex gap-2 justify-between">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrevious}
                disabled={currentPage <= 1}
                className="gap-3 px-3 md:pl-3"
              >
                <ChevronLeft className="h-4 w-4" />
                <span className="hidden md:inline">Назад</span>
              </Button>

              <PaginationContent>
                {getPageNumbers().map((page, index) => {
                  if (page === "ellipsis") {
                    return (
                      <PaginationItem key={`ellipsis-${index}`}>
                        <PaginationEllipsis />
                      </PaginationItem>
                    );
                  }

                  return (
                    <PaginationItem key={page}>
                      <PaginationLink
                        onClick={() => handlePageClick(page)}
                        isActive={page === currentPage}
                        className="cursor-pointer text-sm font-medium leading-5 px-3 min-w-8"
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}
              </PaginationContent>

              <Button
                variant="outline"
                size="sm"
                onClick={handleNext}
                disabled={currentPage >= totalPages}
                className="gap-3 px-3 md:pr-3"
              >
                <span className="hidden md:inline">Вперед</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </Pagination>
          )}
        </div>
      </div>
    );
  },
);

TablePagination.displayName = "TablePagination";
